import React, { useState, useEffect, useRef, useCallback } from 'react';
import OpenSeadragon from 'openseadragon';
import axios from 'axios';
import './index.css';

// API base URL strategy for CORS-free local dev via CRA proxy
const isLocalHost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (isLocalHost ? '' : 'https://path.slidelis.com');

function App() {
  const [slides, setSlides] = useState([]);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [, setZoomLevel] = useState(1);
  const [leicaColorEnabled, setLeicaColorEnabled] = useState(false);
  const [perfLevels, setPerfLevels] = useState(0);
  const [onTheFlyMode, setOnTheFlyMode] = useState(false); // 0 = full, 1 = half-res, 2 = quarter-res
  const prefetchedSlidesRef = useRef(new Set());
  const [labelUrl, setLabelUrl] = useState(null);
  const [macroUrl, setMacroUrl] = useState(null);
  const [, setAssociatedKeys] = useState([]);
  const [labelCollapsed, setLabelCollapsed] = useState(false);
  const [macroCollapsed, setMacroCollapsed] = useState(true);
  // Removed unused slideLabelUrls state
  const [, setViewportBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSlides, setSelectedSlides] = useState(new Set());
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    precacheEnabled: true,
    precacheLevels: 3,
    precacheStrategy: 'smart', // 'off', 'overview', 'smart', 'aggressive'
    maxCacheSize: 1000, // MB
    autoCleanup: true,
    autoPrecacheNewSlides: true
  });
  const viewerRef = useRef(null);
  const osdViewerRef = useRef(null);

  // Trigger precaching for current slide (stable reference for hooks)
  const triggerPrecache = useCallback(async (slideId) => {
    if (!settings.precacheEnabled) return;
    try {
      const strategy = settings.precacheStrategy;
      const levels = settings.precacheLevels;
      await axios.post(`${API_BASE_URL}/slides/${slideId}/precache`, {
        strategy,
        levels,
        maxCacheSize: settings.maxCacheSize
      });
      console.log(`Precaching started for slide ${slideId} with strategy: ${strategy}`);
    } catch (error) {
      console.warn('Precaching failed:', error);
    }
  }, [settings.precacheEnabled, settings.precacheStrategy, settings.precacheLevels, settings.maxCacheSize]);

  // Handle on-the-fly mode toggle
  const handleOnTheFlyModeToggle = async (enabled) => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onTheFlyMode: enabled })
      });
      
      if (response.ok) {
        setOnTheFlyMode(enabled);
        console.log(`[SETTINGS] On-the-fly mode ${enabled ? 'ENABLED' : 'DISABLED'}`);
        if (enabled) {
          console.log('[SETTINGS] 🚀 ON-THE-FLY MODE: All precaching disabled, optimizing for on-demand tile generation');
        } else {
          console.log('[SETTINGS] 💾 CACHE MODE: Precaching enabled, using cached tiles when available');
        }
      } else {
        console.error('Failed to update on-the-fly mode setting');
      }
    } catch (error) {
      console.error('Error updating on-the-fly mode setting:', error);
    }
  };

  // Load settings from backend
  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`);
      if (response.ok) {
        const backendSettings = await response.json();
        setOnTheFlyMode(backendSettings.onTheFlyMode || false);
        console.log('[SETTINGS] Loaded backend settings:', backendSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Fetch slides and settings on component mount
  useEffect(() => {
    fetchSlides();
    loadSettings();
  }, []);


  const fetchSlides = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `${API_BASE_URL}/slides`;
      console.log('[Slides] Requesting:', url);
      let response;
      try {
        response = await axios.get(url, { withCredentials: false });
      } catch (axiosErr) {
        console.warn('[Slides] axios failed, trying fetch fallback', axiosErr?.message);
        try {
          const r = await fetch(url, { method: 'GET' });
          if (!r.ok) {
            const txt = await r.text().catch(() => '');
            console.error('[Slides] fetch error body:', txt);
            throw new Error(`HTTP ${r.status}`);
          }
          const data = await r.json();
          response = { data };
        } catch (fetchErr) {
          throw axiosErr || fetchErr;
        }
      }
      const data = response?.data;
      // Normalize common backend response shapes
      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (data && Array.isArray(data.slides)) {
        list = data.slides;
      } else if (data && data.items && Array.isArray(data.items)) {
        list = data.items;
      } else {
        console.warn('[Slides] Unexpected response shape:', data);
        list = [];
      }
      // Normalize each item to ensure we have id and name fields
      const normalizeSlide = (it) => {
        const id = it?.id ?? it?.slide_id ?? it?.uid ?? it?.identifier ?? it?.name ?? it?.filename;
        const name = it?.name ?? it?.filename ?? it?.id ?? it?.slide_id ?? String(id ?? '');
        return { ...it, id, name };
      };
      list = list.map(normalizeSlide).filter(s => s && s.id);
      console.log(`[Slides] Loaded ${list.length} item(s)`);
      setSlides(list);
    } catch (err) {
      const status = err?.response?.status;
      const statusText = err?.response?.statusText;
      const msg = err?.message || 'unknown error';
      const full = `[Slides] Failed to load (${status || ''} ${statusText || ''}). ${msg}`.trim();
      setError('Failed to load slides. ' + full);
      console.error('Error fetching slides:', err);
    } finally {
      setLoading(false);
    }
  };

  // If slides arrive and nothing is selected yet, select the first to help users
  useEffect(() => {
    if (slides && slides.length > 0 && !selectedSlide) {
      setSelectedSlide(slides[0]);
    }
  }, [slides, selectedSlide]);

  const loadSlide = useCallback(async (slide, forceReload = false) => {
    if (!forceReload && selectedSlide?.id === slide.id) return;

    try {
      setViewerLoading(true);
      setSelectedSlide(slide);
      setError(null);
      
      // Trigger precaching for the selected slide
      triggerPrecache(slide.id);
      // Fetch label image for this slide - force cache refresh
      try {
        const resp = await axios.get(`${API_BASE_URL}/slides/${slide.id}/label?_=${Date.now()}`, { responseType: 'blob' });
        const url = URL.createObjectURL(resp.data);
        setLabelUrl(url);
      } catch (e) {
        console.warn('No label image for slide', e);
        setLabelUrl(null);
      }
      // Destroy existing viewer
      if (osdViewerRef.current) {
        osdViewerRef.current.destroy();
        osdViewerRef.current = null;
      }

      // Check if viewer container exists
      if (!viewerRef.current) {
        setError('Viewer container element not found');
        setViewerLoading(false);
        return;
      }

      // Performance mode: Create custom tile source or modify DZI for lower resolution
      let tileSourceConfig;
      
      if (perfLevels > 0) {
        // For performance mode, create a custom tile source that forces lower resolution
        console.log(`[PERF] Creating custom tile source with performance level ${perfLevels}`);
        
        // Fetch DZI to get slide dimensions
        const dziResponse = await fetch(`${API_BASE_URL}/slides/${slide.id}/dzi.dzi`);
        const dziText = await dziResponse.text();
        const parser = new DOMParser();
        const dziDoc = parser.parseFromString(dziText, 'application/xml');
        const imageEl = dziDoc.getElementsByTagName('Image')[0];
        const sizeEl = imageEl.getElementsByTagName('Size')[0];
        
        const fullWidth = parseInt(sizeEl.getAttribute('Width'));
        const fullHeight = parseInt(sizeEl.getAttribute('Height'));
        const tileSize = parseInt(imageEl.getAttribute('TileSize') || '256');
        const overlap = parseInt(imageEl.getAttribute('Overlap') || '1');
        
        // Calculate reduced resolution based on performance level
        const reductionFactor = Math.pow(2, perfLevels * 2); // 4x, 16x, 64x reduction
        const reducedWidth = Math.max(256, Math.floor(fullWidth / reductionFactor));
        const reducedHeight = Math.max(256, Math.floor(fullHeight / reductionFactor));
        
        // Calculate max level for reduced resolution
        const maxDim = Math.max(reducedWidth, reducedHeight);
        const reducedMaxLevel = Math.ceil(Math.log2(maxDim / tileSize));
        
        console.log(`[PERF] Forcing resolution from ${fullWidth}x${fullHeight} to ${reducedWidth}x${reducedHeight} (${reductionFactor}x reduction)`);
        
        tileSourceConfig = {
          height: reducedHeight,
          width: reducedWidth,
          tileSize: tileSize,
          overlap: overlap,
          minLevel: 0,
          maxLevel: Math.max(0, reducedMaxLevel),
          getTileUrl: function(level, x, y) {
            // Map to appropriate backend level with performance parameters
            const backendLevel = Math.min(level + (perfLevels * 2), 15); // Ensure we don't exceed available levels
            return `${API_BASE_URL}/slides/${slide.id}/dzi_files/${backendLevel}/${x}_${y}.jpeg?perf=${perfLevels}&fast=1&quality=60`;
          }
        };
      } else {
        // Standard mode: use DZI file directly
        tileSourceConfig = `${API_BASE_URL}/slides/${slide.id}/dzi.dzi`;
      }

      // Create new OpenSeadragon viewer
      const viewer = OpenSeadragon({
        element: viewerRef.current,
        prefixUrl: 'https://cdn.jsdelivr.net/npm/openseadragon@4.1.0/build/openseadragon/images/',
        tileSources: tileSourceConfig,
        maxImageCacheCount: 1024,
        homeFillsViewer: false, // ensure whole image fits within viewer
        visibilityRatio: 1.0,
        constrainDuringPan: true,
        // Only use tileUrlFunction for standard mode (non-performance mode)
        ...(perfLevels === 0 && {
          tileUrlFunction: (level, x, y, url) => {
            if (leicaColorEnabled) {
              const separator = url.includes('?') ? '&' : '?';
              url = `${url}${separator}color=leica`;
            }
            return url;
          }
        }),
        showNavigationControl: true,
        showZoomControl: true,
        showHomeControl: true,
        showFullPageControl: true,
        showNavigator: true,
        showRotationControl: false,
        animationTime: 0.3,
        blendTime: 0.1,
        maxZoomPixelRatio: 1.1,
        minZoomImageRatio: 0.8,
        maxZoomLevel: 15,
        zoomPerScroll: 1.4,
        timeout: 120000,
        useCanvas: true,
        immediateRender: true,
        preserveViewport: false,
        // homeFillsViewer already set above
        springStiffness: 6.5,
        imageLoaderLimit: 2,
        loadTilesWhenVisible: true,
        preload: true
      });

      // Handle viewer events
      const fitHome = () => {
        try {
          const bounds = viewer.world.getHomeBounds();
          if (bounds) viewer.viewport.fitBounds(bounds, true);
        } catch {}
      };

      viewer.addHandler('open', () => {
        setViewerLoading(false);
        fitHome(); // fit entire image within viewer
      });

      viewer.addHandler('resize', () => {
        fitHome();
      });

      viewer.addHandler('open-failed', async (event) => {
        setViewerLoading(false);
        setError(`Failed to load slide: ${event.message}`);
        console.error('Failed to load slide:', event, 'tileSources', `${API_BASE_URL}/slides/${slide.id}/dzi.dzi`);
        // Fallback: retry with explicit Deep Zoom object once
        try {
          const dziResp = await fetch(`${API_BASE_URL}/slides/${slide.id}/dzi`);
          const dziText = await dziResp.text();
          console.log('DZI text:', dziText.slice(0, 200));
          const ts = {
            Image: {
              xmlns: 'http://schemas.microsoft.com/deepzoom/2008',
              Url: `${API_BASE_URL}/slides/${slide.id}/dzi_files/`,
              Format: 'jpeg',
              Overlap: 1,
              TileSize: 256,
              Size: { Width: selectedSlide.width, Height: selectedSlide.height }
            }
          };
          console.log('Retrying with explicit tile source');
          viewer.open(ts);
        } catch (e) {
          console.warn('Fallback open failed:', e);
        }
      });

      viewer.addHandler('tile-load-failed', (event) => {
        console.warn('Tile load failed:', event);
      });

      osdViewerRef.current = viewer;
        // Update viewport box on changes
        const updateBox = () => {
          const b = viewer.viewport.getBounds(true); // normalized 0-1
          setViewportBox({ x: b.x, y: b.y, width: b.width, height: b.height });
        };
        viewer.addHandler('viewport-change', updateBox);
        // Initial call
        updateBox();
      // Update zoom level state on zoom events
      viewer.addHandler('zoom', () => {
        const z = viewer.viewport.getZoom();
        setZoomLevel(z);
      });

    } catch (err) {
      setViewerLoading(false);
      setError(`Error loading slide: ${err.message}`);
      console.error('Error loading slide:', err);
    }
  }, [selectedSlide, leicaColorEnabled, perfLevels, triggerPrecache]);

  // Reload viewer when color profile toggles
  useEffect(() => {
    if (!selectedSlide) return;
    setViewerLoading(true);
    loadSlide(selectedSlide, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlide, leicaColorEnabled, perfLevels]);

  // Prefetch low levels for current and next slide into SW cache
  useEffect(() => {
    if (!selectedSlide || !slides || slides.length === 0) return;

    const parseDzi = async (slideId) => {
      const resp = await fetch(`${API_BASE_URL}/slides/${slideId}/dzi`);
      const text = await resp.text();
      const doc = new window.DOMParser().parseFromString(text, 'application/xml');
      const imageEl = doc.getElementsByTagName('Image')[0];
      const sizeEl = imageEl?.getElementsByTagName('Size')[0];
      const width = parseInt(sizeEl?.getAttribute('Width') || '0', 10);
      const height = parseInt(sizeEl?.getAttribute('Height') || '0', 10);
      const format = imageEl?.getAttribute('Format') || 'jpeg';
      const overlap = parseInt(imageEl?.getAttribute('Overlap') || '1', 10);
      const tileSize = parseInt(imageEl?.getAttribute('TileSize') || '256', 10);
      return { width, height, format, overlap, tileSize };
    };

    const calcLevels = (w, h, tileSize) => {
      const maxDim = Math.max(w, h);
      let levels = 0;
      let size = maxDim;
      while (size > 1) {
        size = Math.ceil(size / 2);
        levels += 1;
      }
      return levels;
    };

    const prefetchLevels = async (slideId, levelsToPrefetch = 3) => {
      try {
        const meta = await parseDzi(slideId);
        if (!meta.width || !meta.height) return;
        const maxLevels = calcLevels(meta.width, meta.height, meta.tileSize);
        const targetLevels = Math.min(levelsToPrefetch, maxLevels);
        for (let lvl = 0; lvl < targetLevels; lvl++) {
          const levelWidth = Math.ceil(meta.width / Math.pow(2, maxLevels - lvl - 1));
          const levelHeight = Math.ceil(meta.height / Math.pow(2, maxLevels - lvl - 1));
          const cols = Math.ceil(levelWidth / meta.tileSize);
          const rows = Math.ceil(levelHeight / meta.tileSize);
          for (let c = 0; c < cols; c++) {
            for (let r = 0; r < rows; r++) {
              let url = `${API_BASE_URL}/slides/${slideId}/dzi_files/${lvl}/${c}_${r}.${meta.format}`;
              if (leicaColorEnabled) {
                url = url.includes('?') ? `${url}&color=leica` : `${url}?color=leica`;
              }
              // Fire and forget; SW will cache
              fetch(url).catch(() => {});
            }
          }
        }
      } catch (e) {
        console.warn('Prefetch failed for slide', slideId, e);
      }
    };

    // Prefetch for current slide (ensure fast zoom-out)
    prefetchLevels(selectedSlide.id, 3);
    // Prefetch next slide overview tiles
    const idx = slides.findIndex((s) => s.id === selectedSlide.id);
    if (idx >= 0) {
      const next = slides[(idx + 1) % slides.length];
      if (next && next.id !== selectedSlide.id) {
        prefetchLevels(next.id, 3);
      }
    }
  }, [selectedSlide, slides, leicaColorEnabled]);

  // Load associated images (label/macro) when slide changes
  useEffect(() => {
    if (!selectedSlide) {
      setAssociatedKeys([]);
      setLabelUrl(null);
      setMacroUrl(null);
      return;
    }
    const sid = selectedSlide.id;
    const load = async () => {
      // Set label/macro URLs immediately so requests fire regardless of /associated status
      const bust = Date.now();
      const lbl = `${API_BASE_URL}/slides/${sid}/label?max_width=512&_=${bust}`;
      const mac = `${API_BASE_URL}/slides/${sid}/macro?max_width=1024&_=${bust}`;
      console.log('[Assoc] setting labelUrl', lbl);
      console.log('[Assoc] setting macroUrl', mac);
      setLabelUrl(lbl);
      setMacroUrl(mac);

      try {
        const resp = await fetch(`${API_BASE_URL}/slides/${sid}/associated`);
        if (!resp.ok) throw new Error('assoc not found');
        const data = await resp.json();
        const keys = Array.isArray(data.associated) ? data.associated : [];
        setAssociatedKeys(keys);
      } catch (e) {
        console.warn('[Assoc] associated fetch failed, proceeding without keys');
        setAssociatedKeys([]);
      }
    };
    load();
  }, [selectedSlide]);

  // When new slides appear in the list, prefetch their overview tiles (levels 0..1)
  useEffect(() => {
    if (!slides || slides.length === 0) return;

    const parseDzi = async (slideId) => {
      const resp = await fetch(`${API_BASE_URL}/slides/${slideId}/dzi`);
      const text = await resp.text();
      const doc = new window.DOMParser().parseFromString(text, 'application/xml');
      const imageEl = doc.getElementsByTagName('Image')[0];
      const sizeEl = imageEl?.getElementsByTagName('Size')[0];
      const width = parseInt(sizeEl?.getAttribute('Width') || '0', 10);
      const height = parseInt(sizeEl?.getAttribute('Height') || '0', 10);
      const format = imageEl?.getAttribute('Format') || 'jpeg';
      const tileSize = parseInt(imageEl?.getAttribute('TileSize') || '256', 10);
      return { width, height, format, tileSize };
    };

    const calcLevels = (w, h) => {
      const maxDim = Math.max(w, h);
      let levels = 0;
      let size = maxDim;
      while (size > 1) { size = Math.ceil(size / 2); levels += 1; }
      return levels;
    };

    const prefetchLevels = async (slideId, maxPrefetchLevel = 1) => {
      try {
        const meta = await parseDzi(slideId);
        if (!meta.width || !meta.height) return;
        const maxLevels = calcLevels(meta.width, meta.height);
        const targetLevels = Math.min(maxPrefetchLevel + 1, maxLevels); // include level 0
        for (let lvl = 0; lvl < targetLevels; lvl++) {
          const levelWidth = Math.ceil(meta.width / Math.pow(2, maxLevels - lvl - 1));
          const levelHeight = Math.ceil(meta.height / Math.pow(2, maxLevels - lvl - 1));
          const cols = Math.ceil(levelWidth / meta.tileSize);
          const rows = Math.ceil(levelHeight / meta.tileSize);
          for (let c = 0; c < cols; c++) {
            for (let r = 0; r < rows; r++) {
              let url = `${API_BASE_URL}/slides/${slideId}/dzi_files/${lvl}/${c}_${r}.${meta.format}`;
              if (leicaColorEnabled) {
                url = url.includes('?') ? `${url}&color=leica` : `${url}?color=leica`;
              }
              fetch(url).catch(() => {});
            }
          }
        }
      } catch {}
    };

    const seen = prefetchedSlidesRef.current;
    slides.forEach((s) => {
      if (!s?.id || seen.has(s.id)) return;
      seen.add(s.id);
      // Prefetch minimal overview (levels 0 and 1)
      prefetchLevels(s.id, 1);
    });
  }, [slides, leicaColorEnabled]);

  // Cleanup viewer on unmount
  useEffect(() => {
    return () => {
      if (osdViewerRef.current) {
        osdViewerRef.current.destroy();
      }
    };
  }, []);

  const formatDimensions = (width, height) => {
    return `${width.toLocaleString()} × ${height.toLocaleString()}`;
  };

  // Filter slides based on search term
  const filteredSlides = slides.filter(slide => 
    slide.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle checkbox selection
  const handleSlideSelection = (slideId, checked) => {
    const newSelected = new Set(selectedSlides);
    if (checked) {
      newSelected.add(slideId);
    } else {
      newSelected.delete(slideId);
    }
    setSelectedSlides(newSelected);
  };

  // Handle select all/none
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSlides(new Set(filteredSlides.map(s => s.id)));
    } else {
      setSelectedSlides(new Set());
    }
  };

  // Handle settings changes
  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    // Apply settings to backend
    applySettingsToBackend(newSettings);
  };

  const applySettingsToBackend = async (newSettings) => {
    try {
      await axios.post(`${API_BASE_URL}/settings`, newSettings);
    } catch (error) {
      console.warn('Failed to apply settings to backend:', error);
    }
  };

  

  // Delete selected slides
  const deleteSelectedSlides = async () => {
    if (selectedSlides.size === 0) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete ${selectedSlides.size} slide(s)? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      const slideIds = Array.from(selectedSlides);
      const response = await axios.post(`${API_BASE_URL}/slides/delete-batch`, slideIds);
      
      if (response.data.success) {
        alert(`Successfully deleted ${response.data.count} slide(s)`);
        setSelectedSlides(new Set());
        setIsDeleteMode(false);
        fetchSlides(); // Refresh the list
        
        // Clear current slide if it was deleted
        if (selectedSlide && selectedSlides.has(selectedSlide.id)) {
          setSelectedSlide(null);
          setLabelUrl(null);
          setMacroUrl(null);
          if (osdViewerRef.current) {
            osdViewerRef.current.destroy();
            osdViewerRef.current = null;
          }
        }
      } else {
        alert(`Deleted ${response.data.count} slide(s). Errors: ${response.data.errors.join(', ')}`);
      }
    } catch (error) {
      alert(`Error deleting slides: ${error.message}`);
    }
  };

  return (
    <div className="app">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h1>Pathology Viewer</h1>
          
          {/* Search Bar */}
          <div style={{ marginTop: '15px' }}>
            <input
              type="text"
              placeholder="Search slides (e.g., case number)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #4a5f7a',
                borderRadius: '4px',
                backgroundColor: '#2c3e50',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Delete Controls */}
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <button 
                  onClick={() => setIsDeleteMode(!isDeleteMode)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isDeleteMode ? '#e74c3c' : '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    flex: 1
                  }}
                >
                  {isDeleteMode ? 'Exit Delete Mode' : 'Delete Mode'}
                </button>
                
                <button 
                  onClick={() => setShowSettings(!showSettings)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: showSettings ? '#9b59b6' : '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    flex: 1
                  }}
                >
                  {showSettings ? 'Hide Settings' : 'Settings'}
                </button>
              </div>
            
            {isDeleteMode && (
              <>
                <button
                  onClick={() => handleSelectAll(selectedSlides.size !== filteredSlides.length)}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#3498db',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {selectedSlides.size === filteredSlides.length ? 'None' : 'All'}
                </button>
                
                {selectedSlides.size > 0 && (
                  <button
                    onClick={deleteSelectedSlides}
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete ({selectedSlides.size})
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="sidebar-content">
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              Loading slides...
            </div>
          ) : error && slides.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#e74c3c' }}>
              {error}
              <br />
              <button 
                onClick={fetchSlides}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : slides.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>
              No slides found. Place .svs files in the backend/slides/ directory.
            </div>
          ) : (
            <>
              {/* Search Results Info */}
              {searchTerm && (
                <div style={{ 
                  padding: '10px 20px', 
                  fontSize: '12px', 
                  color: '#bdc3c7',
                  borderBottom: '1px solid #34495e'
                }}>
                  {filteredSlides.length} of {slides.length} slides match "{searchTerm}"
                </div>
              )}
              
              {/* Settings Panel */}
              {showSettings && (
                <div style={{
                  backgroundColor: '#34495e',
                  border: '1px solid #4a5568',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#ecf0f1', fontSize: '16px' }}>
                    🚀 Performance Settings
                  </h3>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#bdc3c7' }}>
                      <input
                        type="checkbox"
                        checked={settings.precacheEnabled}
                        onChange={(e) => updateSettings({ precacheEnabled: e.target.checked })}
                      />
                      Enable Smart Precaching
                    </label>
                  </div>
                  
                  {settings.precacheEnabled && (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ color: '#bdc3c7', display: 'block', marginBottom: '4px' }}>
                          Precache Strategy:
                        </label>
                        <select
                          value={settings.precacheStrategy}
                          onChange={(e) => updateSettings({ precacheStrategy: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '6px',
                            backgroundColor: '#2c3e50',
                            color: '#ecf0f1',
                            border: '1px solid #4a5568',
                            borderRadius: '4px'
                          }}
                        >
                          <option value="overview">Overview Only (Fast, Low Space)</option>
                          <option value="smart">Smart Adaptive (Balanced)</option>
                          <option value="aggressive">Aggressive (Slow, High Space)</option>
                        </select>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ color: '#bdc3c7', display: 'block', marginBottom: '4px' }}>
                          Cache Levels: {settings.precacheLevels}
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="6"
                          value={settings.precacheLevels}
                          onChange={(e) => updateSettings({ precacheLevels: parseInt(e.target.value) })}
                          style={{ width: '100%' }}
                        />
                        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                          More levels = better zoom performance, more storage
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ color: '#bdc3c7', display: 'block', marginBottom: '4px' }}>
                          Max Cache Size: {settings.maxCacheSize} MB
                        </label>
                        <input
                          type="range"
                          min="100"
                          max="5000"
                          step="100"
                          value={settings.maxCacheSize}
                          onChange={(e) => updateSettings({ maxCacheSize: parseInt(e.target.value) })}
                          style={{ width: '100%' }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#bdc3c7' }}>
                          <input
                            type="checkbox"
                            checked={settings.autoCleanup}
                            onChange={(e) => updateSettings({ autoCleanup: e.target.checked })}
                          />
                          Auto-cleanup old cache files
                        </label>
                      </div>
                      
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#bdc3c7' }}>
                          <input
                            type="checkbox"
                            checked={settings.autoPrecacheNewSlides}
                            onChange={(e) => updateSettings({ autoPrecacheNewSlides: e.target.checked })}
                          />
                          Auto-precache new slides when added
                        </label>
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginLeft: '24px' }}>
                          Automatically start precaching when new slides are detected
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {filteredSlides.length === 0 && searchTerm ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>
                  No slides match your search.
                </div>
              ) : (
                <ul className="slide-list">
              {filteredSlides.map((slide) => (
                <li
                  key={slide.id}
                  className={selectedSlide?.id === slide.id ? 'selected' : ''}
                  onClick={(e) => {
                    if (isDeleteMode && e.target.type === 'checkbox') {
                      return; // Don't load slide when clicking checkbox
                    }
                    if (!isDeleteMode) {
                      loadSlide(slide);
                    }
                  }}
                  style={{
                    cursor: isDeleteMode ? 'default' : 'pointer',
                    opacity: isDeleteMode && selectedSlides.has(slide.id) ? 0.7 : 1
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Checkbox for delete mode */}
                    {isDeleteMode && (
                      <input
                        type="checkbox"
                        checked={selectedSlides.has(slide.id)}
                        onChange={(e) => handleSlideSelection(slide.id, e.target.checked)}
                        style={{ 
                          width: '16px', 
                          height: '16px',
                          flexShrink: 0
                        }}
                      />
                    )}
                    
                    {/* Label thumbnail next to slide name */}
                    <img
                      src={`${API_BASE_URL}/slides/${slide.id}/label?max_width=200&_=${Date.now()}`}
                      alt=""
                      style={{ 
                        width: 120, 
                        height: 120, 
                        objectFit: 'cover', 
                        border: '2px solid #ddd', 
                        borderRadius: 6,
                        flexShrink: 0
                      }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <div>
                      <div className="slide-name">{slide.name}</div>
                      <div className="slide-info">
                        {formatDimensions(slide.width, slide.height)}
                        <br />
                        {slide.levels} levels • {slide.format.toUpperCase()}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
             </ul>
              )}
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {selectedSlide && (
          <div className="viewer-header">
            <h2>{selectedSlide.name}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={leicaColorEnabled}
                  onChange={(e) => setLeicaColorEnabled(e.target.checked)}
                />
                <span style={{ marginLeft: 6 }}>Leica color profile</span>
              </label>
              <label style={{ marginLeft: 12 }}>
                <select
                  value={perfLevels}
                  onChange={(e) => setPerfLevels(parseInt(e.target.value))}
                  style={{ marginLeft: 6 }}
                >
                  <option value={0}>Full Quality</option>
                  <option value={1}>High Performance (2x faster)</option>
                  <option value={2}>Max Performance (4x faster)</option>
                  <option value={3}>Ultra Performance (8x faster)</option>
                </select>
              </label>
              <label style={{ marginLeft: 12 }}>
                <input
                  type="checkbox"
                  checked={onTheFlyMode}
                  onChange={(e) => handleOnTheFlyModeToggle(e.target.checked)}
                />
                <span style={{ marginLeft: 6 }}>🚀 On-the-fly mode (no precaching)</span>
              </label>
              {/* Quick access to open label in new tab for debugging */}
              <a
                href={`${API_BASE_URL}/slides/${selectedSlide.id}/label`}
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: 12, fontSize: 13, textDecoration: 'underline' }}
                onClick={() => console.log('[Assoc] header label link clicked')}
              >
                Open label
              </a>
            </div>
          </div>
        )}

        {/* Viewer region with overlay anchor */}
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {/* Always render the viewer container, but hide it when not needed */}
          <div 
            ref={viewerRef} 
            className="openseadragon-viewer"
            style={{ 
              width: '100%', 
              height: '100%',
              display: selectedSlide && !viewerLoading && !error ? 'block' : 'none'
            }}
          />
          {/* Collapsible Label overlay positioned under zoom controls */}
          {labelUrl && selectedSlide && (
            <div style={{ position: 'absolute', top: 56, left: 8, zIndex: 1000 }}>
              <div style={{ 
                backgroundColor: 'rgba(255,255,255,0.9)', 
                border: '1px solid #ddd', 
                borderRadius: 4,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                <button
                  onClick={() => setLabelCollapsed(!labelCollapsed)}
                  style={{
                    width: '100%',
                    padding: '4px 8px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  Label {labelCollapsed ? '▶' : '▼'}
                </button>
                {!labelCollapsed && (
                  <a
                    href={`${API_BASE_URL}/slides/${selectedSlide.id}/label`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'block', padding: '4px' }}
                  >
                    <img
                      src={labelUrl}
                      alt="Label"
                      style={{ 
                        width: 240, 
                        height: 'auto', 
                        display: 'block'
                      }}
                      onError={(e) => { e.currentTarget.parentElement.parentElement.parentElement.style.display = 'none'; }}
                    />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Collapsible Macro overlay positioned under label */}
          {macroUrl && selectedSlide && (
            <div style={{ position: 'absolute', top: labelCollapsed ? 88 : 240, left: 8, zIndex: 1000 }}>
              <div style={{ 
                backgroundColor: 'rgba(255,255,255,0.9)', 
                border: '1px solid #ddd', 
                borderRadius: 4,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                <button
                  onClick={() => setMacroCollapsed(!macroCollapsed)}
                  style={{
                    width: '100%',
                    padding: '4px 8px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  Macro {macroCollapsed ? '▶' : '▼'}
                </button>
                {!macroCollapsed && (
                  <a
                    href={`${API_BASE_URL}/slides/${selectedSlide.id}/macro`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'block', padding: '4px' }}
                  >
                    <img
                      src={macroUrl}
                      alt="Macro"
                      style={{ 
                        width: 200, 
                        height: 'auto', 
                        display: 'block'
                      }}
                      onError={(e) => { e.currentTarget.parentElement.parentElement.parentElement.style.display = 'none'; }}
                    />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Slide name overlay */}
        {selectedSlide && (
          <div className="slide-label" style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            background: 'rgba(0,0,0,0.5)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.9rem',
            pointerEvents: 'none'
          }}>
            {selectedSlide.name}
          </div>
        )}
      </div>

      {/* Connection tester removed per request */}
    </div>
  );
}

export default App;
